import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { jwtDecode } from 'jwt-decode'; // You'll need to install this: npm install jwt-decode
import settingsService from '../../../settings/settingsService';
import { useNotifications } from './notification/NotificationContext';
import clearUserData from '../clearUserData';
import { refreshAccessToken, retryWithRefreshedToken } from '../authRefresh';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { notificationError } = useNotifications();

  const fetchCurrentUser = async () => {
    try {
      setLoading(true);
      const response = await api.call.get('users/current');
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error("Error fetching current user:", err);
      setError(err);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Permission check functions based on userGroupName (not userGroupFk)
  const isAdmin = () => {
    return user?.userGroupName === 'Admins';
  };

  const canModifyData = () => {
    // Admin, General Users, and Attorneys can modify data
    return user?.userGroupName === 'Admins'
      || user?.userGroupName === 'General Users'
      || user?.userGroupName === 'Attorneys';
  };

  const canAccessMaintenance = () => {
    return user?.userGroupName === 'Admins'; // Admin only
  };

  const canAccessSettings = () => {
    return user?.userGroupName === 'Admins'; // Admin only
  };

  const canAccessTransactionLog = () => {
    return user?.userGroupName === 'Admins'; // Admin only
  };

  const isReadOnly = () => {
    return user?.userGroupName === 'Read-Only';
  };

  const isAttorney = () => {
    return user?.userGroupName === 'Attorneys';
  };

  const isGeneralUser = () => {
    return user?.userGroupName === 'General Users';
  };

  // Helper to get role type (matches what dashboard expects)
  const getUserRole = () => {
    if (!user?.userGroupName) return 'Unknown';

    switch (user.userGroupName) {
      case 'Admins':
        return 'Admin';
      case 'General Users':
        return 'GeneralUser';
      case 'Attorneys':
        return 'Attorney';
      case 'Read-Only':
        return 'ReadOnly';
      default:
        return 'Unknown';
    }
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch (e) {
      return true;
    }
  };

  // Try to refresh the token (shared with any refresh already in flight — see authRefresh.js)
  const refreshToken = async () => Boolean(await refreshAccessToken());

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const responseInterceptor = api.call.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          // Refresh and replay; if the session can't be renewed, log out.
          return retryWithRefreshedToken(error, () => setUser(null));
        } else if (error.response?.status === 403) {
          // Permission-denied is not handled anywhere else in the app today —
          // without this, a 403 fails silently (console error at best).
          notificationError("You don't have permission to perform this action.");
        } else if (!error.response) {
          // No response at all means the request never reached the API (network
          // down, API unreachable, CORS failure, etc.) — also unhandled elsewhere.
          notificationError('Unable to reach the server. Please check your connection and try again.');
        }

        return Promise.reject(error);
      }
    );
    
    // Cleanup function
    return () => {
      api.call.interceptors.response.eject(responseInterceptor);
    };
  }, [notificationError]);

  // Load reference data from API
  const loadReferenceData = async () => {
    try {
      const [requesters, fileStatus, departments, offices, states, typeoflaw, media] = await Promise.all([
        api.call.get("requesters?pageSize=1000&sortField=lastName"),
        api.call.get("fileStatus?pageSize=1000&sortField=Description"),
        api.call.get("departments?pageSize=1000&sortField=Description"),
        api.call.get("offices?pageSize=1000&sortField=Description"),
        api.call.get("states?pageSize=1000&sortField=Name"),
        api.call.get("typeoflaw?pageSize=1000&sortField=Description"),
        api.call.get("media?pageSize=1000&sortField=Description")
      ]);

      localStorage.setItem("ft-requesters", JSON.stringify(requesters.data.response));
      localStorage.setItem("ft-fileStatus", JSON.stringify(fileStatus.data.response));
      localStorage.setItem("ft-departments", JSON.stringify(departments.data.response));
      localStorage.setItem("ft-offices", JSON.stringify(offices.data.response));
      localStorage.setItem("ft-states", JSON.stringify(states.data.response));
      localStorage.setItem("ft-typeoflaw", JSON.stringify(typeoflaw.data.response));
      localStorage.setItem("ft-media", JSON.stringify(media.data.response));
    } catch (err) {
      console.error("Failed to load reference data:", err);
      // Don't fail login if reference data fails to load
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      // Start clean even if the previous session never logged out (closed tab,
      // expired token): nothing cached from another user or firm may survive into
      // this one — e.g. if the org-settings load below fails, the previous org's
      // hierarchy mode must not be what's left in place.
      clearUserData();
      delete api.call.defaults.headers.common['Authorization'];
      const response = await api.call.post('users/apiusers/token', credentials);

      // Store tokens
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('refresh_token', response.data.refreshToken);
      localStorage.setItem('token_expiry', response.data.expiration);

      // Set Authorization header
      api.call.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      // Get user data
      const userData = await fetchCurrentUser();

      // Load reference data after authentication
      await loadReferenceData();

      // Load settings
      const settingsResponse = await settingsService.getAllSettings();
      localStorage.setItem('settings', JSON.stringify(settingsResponse));

      // Load org-level settings (e.g. 2-level vs 3-level inventory hierarchy) —
      // drives UI rendering only; the API enforces the real boundary independently.
      try {
        const organizationSettings = await settingsService.getOrganizationSettings();
        localStorage.setItem('organizationSettings', JSON.stringify(organizationSettings));
      } catch (err) {
        console.error("Failed to load organization settings:", err);
        // Don't fail login if this fails — settingsService/isTwoLevelMode fall back
        // to the ClientMatterFolder default when nothing is cached.
      }

      return { success: true, user: userData };
    } catch (err) {
      console.error("Login failed:", err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Try to revoke the token on the server
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.call.post('users/apiusers/revoke-token', { 
          refreshToken 
        }).catch(err => console.warn("Token revocation failed:", err));
      }
    } finally {
      // Clear tokens, cached org data and user state regardless of server response
      clearUserData();
      delete api.call.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  // Check for existing token on startup
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const tokenExpiry = localStorage.getItem('token_expiry');

      if (token) {
        // Check if token is expired or close to expiry
        const isExpired = tokenExpiry ? new Date(tokenExpiry) <= new Date() : true;

        if (isExpired) {
          // Try to refresh the token
          const refreshed = await refreshToken();
          if (refreshed) {
            api.call.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('auth_token')}`;
            await fetchCurrentUser();
            await loadReferenceData(); 
          } else {
            // If refresh failed, clear everything
            clearUserData();
            setUser(null);
          }
        } else {
          // Token is still valid
          api.call.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          await fetchCurrentUser();
          await loadReferenceData(); // ADD THIS LINE
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      loading,
      error,
      login,
      logout,
      fetchCurrentUser,
      refreshToken,
      isAdmin,
      canModifyData,
      canAccessMaintenance,
      canAccessSettings,
      canAccessTransactionLog,
      isReadOnly
    }}>
      {children}
    </UserContext.Provider>
  );
};